import React, { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { GET_STAFF_DETAILS } from "../../../../../apis/organization-apis/people-apis/peopleApis";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";

export default function BulkUserAccessDialog({ formData, setFormData, theme, existingUsers = [], inScopeOnly = false }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [userRoles, setUserRoles] = useState({});

  const { data: staffData, loading } = useQuery(GET_STAFF_DETAILS, {
    variables: inScopeOnly ? { filter: { in_scope: true } } : {},
  });
  const employees = staffData?.getEmployees?.data || [];

  const allUsers = useMemo(() => {
    return employees.map((emp, idx) => {
      const primaryEmail = emp.emails?.find((em) => em.type === "work")?.email || emp.emails?.[0]?.email || "";
      return {
        id: emp.id || idx + 1,
        name: emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
        email: primaryEmail,
      };
    });
  }, [employees]);

  const filteredUsers = useMemo(() => {
    const searchFiltered = searchQuery
      ? allUsers.filter(
        (user) =>
          user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
      : allUsers;

    // Remove users that already exist in the system
    const existingUserEmails = new Set(existingUsers.map(u => u.email?.toLowerCase()).filter(Boolean));

    return searchFiltered.filter(user =>
      !existingUserEmails.has(user.email?.toLowerCase())
    );
  }, [allUsers, searchQuery, existingUsers]);

  const columns = [
    { label: "Name", field: "name", width: "30%" },
    { label: "Email", field: "email", width: "35%" },
    { label: "System Role", field: "role", width: "25%" },
    { label: "Action", field: "action", width: "10%" },
  ];

  const handleRoleChange = (userId, role) => {
    setUserRoles((prev) => ({ ...prev, [userId]: role }));
  };

  const handleAddSingleUser = (userId) => {
    const user = allUsers.find((u) => u.id === userId);
    const role = userRoles[userId];

    if (user && role && role.trim()) {
      setFormData((prev) => ({
        ...prev,
        users: [...prev.users, { ...user, role: role.trim() }],
      }));

      // Clear the role for this user after adding
      setUserRoles((prev) => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
    }
  };

  const handleRemoveUser = (userId) => {
    setFormData((prev) => ({
      ...prev,
      users: prev.users.filter((u) => u.id !== userId),
    }));
  };

  const renderCell = (row, column) => {
    const isUserAdded = formData.users.some(u => u.id === row.id);
    const addedUser = formData.users.find(u => u.id === row.id);

    if (column.field === "role") {
      if (isUserAdded) {
        return (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            {addedUser.role}
          </Typography>
        );
      }
      return (
        <TextField
          placeholder="Enter role"
          value={userRoles[row.id] || ""}
          onChange={(e) => handleRoleChange(row.id, e.target.value)}
          size="small"
          sx={{ minWidth: 130 }}
        />
      );
    }

    if (column.field === "action") {
      if (isUserAdded) {
        return (
          <IconButton
            onClick={() => handleRemoveUser(row.id)}
            color="error"
            size="small"
            sx={{
              "&:hover": {
                backgroundColor: '#FEE2E2',
              },
            }}
          >
            <RemoveIcon />
          </IconButton>
        );
      }

      const role = userRoles[row.id];
      const hasRole = role && role.trim().length > 0;

      return (
        <IconButton
          onClick={() => handleAddSingleUser(row.id)}
          disabled={!hasRole}
          color="primary"
          size="small"
          sx={{
            opacity: hasRole ? 1 : 0.5,
            "&:hover": {
              opacity: hasRole ? 0.8 : 0.5,
            },
          }}
        >
          <AddIcon />
        </IconButton>
      );
    }

    return row[column.field];
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="h5" fontWeight={600}>
          Add Users
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formData.users.length} already added • {filteredUsers.length} available users
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search users..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ width: '300px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <ReusableTable
        columns={columns}
        data={filteredUsers}
        renderCell={renderCell}
        loading={loading}
        height="400px"
        showPagination={true}
        enableHorizontalScroll={true}
      />
    </Box>
  );
}